import { html, attr, prop, Signal, When, Value, Fragment } from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { Button, Icon } from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { DataTable } from '@/components/data'
import { LoadingState } from '@/components/ui/loading-state'
import { ErrorState } from '@/components/ui/error-state'
import { StatusBadge, type BadgeVariant } from '@/components/ui/status-badge'
import { formatDate, capitalize } from '@/utils/formatting'
import { organizations as orgsService, authStore } from '@/services/gateway'

// ============================================================================
// Types
// ============================================================================

interface Organization {
  id: string
  name: string
  isPersonal: boolean
}

interface Member {
  id: string
  userId: string
  email: string
  displayName: string
  role: 'owner' | 'admin' | 'member' | 'viewer'
  status: 'active' | 'pending' | 'revoked'
  joinedAt: string
}

// ============================================================================
// Organization Members Page Component
// ============================================================================

export interface OrganizationMembersPageOptions {
  id: Value<string>
  memberId?: Value<string>
}

export function OrganizationMembersPage({
  id,
  memberId,
}: OrganizationMembersPageOptions) {
  const org = prop<Organization | null>(null)
  const members = prop<Member[]>([])
  const isLoading = prop(true)
  const error = prop<string | null>(null)
  const isOwnerOrAdmin = prop(false)

  // Load data
  loadOrganization()

  async function loadOrganization() {
    isLoading.set(true)
    error.set(null)

    try {
      const orgId = typeof id === 'string' ? id : id.get()
      const orgData = await orgsService.getOrganization(orgId)

      // Get members
      const membersList: Member[] = []
      try {
        const membersData = await orgsService.listMembers(orgId)

        // Map members directly - API now returns email and displayName
        for (let idx = 0; idx < membersData.members.length; idx++) {
          const m = membersData.members[idx]
          membersList.push({
            id: m.id || `member_${idx}`,
            userId: m.userId,
            email: m.email || m.userId,
            displayName: m.displayName || m.email || m.userId,
            role: m.role as 'owner' | 'admin' | 'member' | 'viewer',
            status: m.status as 'active' | 'pending' | 'revoked',
            joinedAt: m.joinedAt || '',
          })
        }
      } catch {
        // Ignore member fetch errors
      }

      org.set({
        id: orgData.id,
        name: orgData.name,
        isPersonal: orgData.isPersonal,
      })
      members.set(membersList)

      // Check current user's role
      const currentUserId = authStore.user.get()?.id
      const currentMember = membersList.find((m) => m.userId === currentUserId)
      isOwnerOrAdmin.set(
        currentMember?.role === 'owner' || currentMember?.role === 'admin'
      )
    } catch (err) {
      error.set(
        err instanceof Error ? err.message : 'Failed to load organization'
      )
    } finally {
      isLoading.set(false)
    }
  }

  const orgId = typeof id === 'string' ? id : id.get()

  return PageLayout({
    header: html.div(
      attr.class('flex items-center gap-3'),
      Anchor(
        { href: `/o/${orgId}` },
        attr.class('text-gray-400 hover:text-gray-600'),
        Icon({ icon: 'mdi:chevron-left' }, attr.class('w-5 h-5'))
      ),
      html.span(
        org.map((o) => o?.name || 'Organization'),
        html.span(attr.class('text-gray-400'), ' / Members')
      )
    ),
    content: Fragment(
      // Loading state
      When(isLoading, () => LoadingState({ size: 'lg' })),
      // Error state
      When(
        error.map((e) => e !== null),
        () => ErrorState({ error })
      ),
      // Main content
      When(
        org.map((o) => o !== null),
        () =>
          html.div(
            attr.class('space-y-6'),
            // Header with invite button
            html.div(
              attr.class('flex items-center justify-between'),
              html.p(
                attr.class('text-sm text-gray-600'),
                'Manage organization members and permissions'
              ),
              When(isOwnerOrAdmin, () =>
                Button(
                  {},
                  Icon(
                    { icon: 'mdi:account-plus' },
                    attr.class('w-4 h-4 mr-2')
                  ),
                  'Invite Member'
                )
              )
            ),
            // Members table
            html.div(
              attr.class(
                'bg-white rounded-xl border border-gray-200 overflow-hidden'
              ),
              DataTable<Member>({
                columns: [
                  {
                    key: 'email',
                    header: 'Member',
                    render: (item) => {
                      const isHighlighted = memberId
                        ? item.map((m) => {
                            const targetId =
                              typeof memberId === 'string'
                                ? memberId
                                : memberId.get()
                            return m.userId === targetId
                          })
                        : prop(false)

                      return html.div(
                        attr.class(
                          isHighlighted.map((highlighted) =>
                            highlighted
                              ? 'pl-3 border-l-4 border-brand-600'
                              : ''
                          )
                        ),
                        html.p(
                          attr.class('font-medium text-gray-900'),
                          item.map((m) => m.displayName)
                        ),
                        html.p(
                          attr.class('text-sm text-gray-500'),
                          item.map((m) => m.email)
                        ),
                        html.p(
                          attr.class('text-sm text-gray-500'),
                          'Joined ',
                          item.map((m) =>
                            m.joinedAt ? formatDate(m.joinedAt) : 'N/A'
                          )
                        )
                      )
                    },
                  },
                  {
                    key: 'role',
                    header: 'Role',
                    render: (item) =>
                      RoleBadge(
                        item.map((m) => m.role) as Signal<
                          'owner' | 'admin' | 'member'
                        >
                      ),
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (item) =>
                      MemberStatusBadge(
                        item.map((m) => m.status) as Signal<
                          'active' | 'pending' | 'disabled'
                        >
                      ),
                  },
                  {
                    key: 'id',
                    header: '',
                    render: (item) =>
                      When(isOwnerOrAdmin, () =>
                        Anchor(
                          {
                            href: item.map(
                              (m) => `/o/${orgId}/members/${m.userId}`
                            ),
                          },
                          attr.class(
                            'text-brand-600 hover:text-brand-700 text-sm font-medium'
                          ),
                          'Manage'
                        )
                      ),
                  },
                ],
                data: members,
                renderMobileCard: (member) =>
                  MemberCard(member, isOwnerOrAdmin, orgId, memberId),
                emptyMessage: 'No members yet.',
              })
            )
          )
      )
    ),
  })
}

// ============================================================================
// Utility Components
// ============================================================================

function RoleBadge(role: Signal<'owner' | 'admin' | 'member'>) {
  const variantMap: Record<string, BadgeVariant> = {
    owner: 'purple',
    admin: 'info',
    member: 'neutral',
  }

  return StatusBadge({
    label: role.map(capitalize),
    variant: role.map((r) => variantMap[r] ?? 'neutral'),
  })
}

function MemberStatusBadge(status: Signal<'active' | 'pending' | 'disabled'>) {
  const variantMap: Record<string, BadgeVariant> = {
    active: 'success',
    pending: 'warning',
    disabled: 'error',
    revoked: 'error',
  }

  return StatusBadge({
    label: status.map(capitalize),
    variant: status.map((s) => variantMap[s] ?? 'neutral'),
  })
}

function MemberCard(
  member: Signal<Member>,
  isOwnerOrAdmin: Signal<boolean>,
  orgId: string,
  memberId?: Value<string>
) {
  // Check if this member is the highlighted one
  const isHighlighted = memberId
    ? member.map((m) => {
        const targetId =
          typeof memberId === 'string' ? memberId : memberId.get()
        return m.userId === targetId
      })
    : prop(false)

  return html.div(
    attr.class(
      isHighlighted.map((highlighted) =>
        highlighted
          ? 'bg-brand-50 border-l-4 border-brand-600 border border-gray-200 rounded-lg p-4'
          : 'bg-white border border-gray-200 rounded-lg p-4'
      )
    ),
    html.div(
      attr.class('flex items-start justify-between mb-3'),
      html.div(
        html.h3(
          attr.class('font-medium text-gray-900'),
          member.map((m) => m.displayName)
        ),
        html.p(
          attr.class('text-sm text-gray-500'),
          member.map((m) => m.email)
        ),
        html.p(
          attr.class('text-sm text-gray-500 mt-1'),
          'Joined ',
          member.map((m) => (m.joinedAt ? formatDate(m.joinedAt) : 'N/A'))
        )
      ),
      When(isOwnerOrAdmin, () =>
        Anchor(
          { href: member.map((m) => `/o/${orgId}/members/${m.userId}`) },
          attr.class('text-brand-600 hover:text-brand-700'),
          Icon({ icon: 'mdi:dots-vertical' }, attr.class('w-5 h-5'))
        )
      )
    ),
    html.div(
      attr.class('flex items-center gap-2'),
      RoleBadge(
        member.map((m) => m.role) as Signal<'owner' | 'admin' | 'member'>
      ),
      MemberStatusBadge(
        member.map((m) => m.status) as Signal<'active' | 'pending' | 'disabled'>
      )
    )
  )
}
